import { useSuspenseQuery } from '@apollo/client';
import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { EntityPageFocusType } from '@xing-com/crate-common-graphql-types';
import { useLocation } from '@xing-com/crate-core-router';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useFeatureSwitch, useViewerData } from '@xing-com/hub';

import { AppcuesGetCompanyInfoDocument } from '../../graphql/queries/appcues-get-company-info.gql-types';
import * as appcues from '../../utils/appcues';

const APPCUES_FOCUSTYPES: EntityPageFocusType[] = [EntityPageFocusType.Company];
export const APPCUES_FEATURESWITCH = 'use_appcues';

export const Appcues: FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const intl = useIntl();
  const { data: { user } = {} } = useViewerData();

  const { pageContext } = usePageContext();

  const companyId = pageContext.companyId;
  const focusType = pageContext.focusType;

  const hasFeatureSwitch = useFeatureSwitch(APPCUES_FEATURESWITCH, false);
  const hasFocusType = Boolean(APPCUES_FOCUSTYPES.includes(focusType));
  const hasRole = Boolean(pageContext.isEditor);

  const hasAppcues = hasFeatureSwitch && hasFocusType && hasRole;

  const { data } = useSuspenseQuery(AppcuesGetCompanyInfoDocument, {
    variables: {
      id: companyId ?? '',
    },
    errorPolicy: 'all',
    skip: !hasAppcues || !companyId,
  });

  useEffect(() => {
    const appcuesCompanyData = {
      companyId: companyId ?? '',
      permissions: data?.company?.userContext?.userRole ?? '',
      companyName: data?.company?.companyName ?? '',
      contractType: data?.company?.contractPackageName ?? '',
      companySizeId: data?.company?.companySizeId ?? '',
      companySize: data?.company?.companySize ?? '',
      createdDate: data?.company?.createdAt ?? '',
      hasXamAccount: data?.company?.hasXamAccount ?? '',
      industry: data?.company?.industry?.localizationValue ?? '',
      industryId: data?.company?.industry?.id ?? '',
      language: intl?.locale ?? '',
      pageSlug: pageContext.pageSlug ?? '',
    };

    const element = appcues.getElement();
    const isLoaded = appcues.isLoaded();

    if (hasAppcues && user) {
      const id = String(user?.id);

      if (!element) {
        appcues.createElement(id, appcuesCompanyData);
      } else if (isLoaded) {
        appcues.loadResources(id, appcuesCompanyData);
      }
    } else {
      if (element) {
        appcues.deleteElement();
        if (isLoaded) appcues.resetResources();
      }
    }

    return () => {
      const isLoaded = appcues.isLoaded();
      if (hasAppcues && isLoaded) {
        appcues.resetResources();
      }
    };
  }, [hasAppcues, user, pathname, data]);

  return children;
};
